import { useState } from 'react';
import axios from 'axios';
import { Phone, Mail, MapPin, Send, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import Reveal from './Reveal';

const API = process.env.REACT_APP_BACKEND_URL;

const CARDS = [
  {
    id: 'phone',
    icon: Phone,
    label: 'Call us',
    value: '0473 619 278',
    href: 'tel:0473619278',
    sub: 'Mon – Fri, or leave a message',
  },
  {
    id: 'email',
    icon: Mail,
    label: 'Email us',
    value: 'Careforall.ss@outlook.com',
    href: 'mailto:Careforall.ss@outlook.com',
    sub: 'We reply as soon as we can',
  },
  {
    id: 'address',
    icon: MapPin,
    label: 'Find us',
    value: '2/114 Byrnes St, Mareeba QLD 4880',
    href: 'https://maps.google.com/?q=2/114+Byrnes+St,+Mareeba+QLD+4880',
    sub: 'In the heart of Mareeba',
  },
];

export default function Contact() {
  const [form, setForm] = useState({ name: '', contact: '', message: '' });
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    try {
      await axios.post(`${API}/api/enquiry`, form);
      toast.success("Thanks! Your enquiry has been sent — we'll be in touch soon.");
      setForm({ name: '', contact: '', message: '' });
    } catch {
      toast.error('Something went wrong. Please call us on 0473 619 278 instead.');
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" className="relative py-24 lg:py-32 bg-gradient-to-b from-brand-sky via-white to-brand-cream">
      <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
        <Reveal className="text-center max-w-2xl mx-auto">
          <p className="text-xs uppercase tracking-[0.28em] font-semibold text-sky-700">Get in touch</p>
          <h2 className="mt-4 font-display font-extrabold tracking-tight text-4xl sm:text-5xl text-slate-900 leading-[1.08]">
            Let&apos;s talk about <span className="font-accent italic font-normal text-azure">your support</span>
          </h2>
          <p className="mt-5 text-lg text-slate-600 leading-relaxed">
            Call, email or send us a message — a real person from our team will get back to you.
          </p>
        </Reveal>

        <div className="mt-14 grid lg:grid-cols-5 gap-8 items-start">
          <div className="lg:col-span-2 space-y-4">
            {CARDS.map((c, i) => (
              <Reveal key={c.id} delay={i * 0.1}>
                <a
                  data-testid={`contact-card-${c.id}`}
                  href={c.href}
                  target={c.id === 'address' ? '_blank' : undefined}
                  rel={c.id === 'address' ? 'noreferrer' : undefined}
                  className="group flex items-start gap-4 rounded-2xl bg-white border border-sky-100 p-6 shadow-soft transition-all hover:shadow-lift hover:-translate-y-1"
                >
                  <span className="grid place-items-center h-12 w-12 rounded-2xl bg-gradient-to-br from-sky-500 to-cyan-400 text-white shrink-0 transition-transform duration-300 group-hover:scale-110 group-hover:rotate-6">
                    <c.icon size={20} />
                  </span>
                  <span>
                    <span className="block text-[11px] uppercase tracking-[0.22em] font-semibold text-sky-700">
                      {c.label}
                    </span>
                    <span className="block mt-1 font-display font-bold text-slate-900 break-all">{c.value}</span>
                    <span className="block mt-0.5 text-sm text-slate-500">{c.sub}</span>
                  </span>
                </a>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.15} className="lg:col-span-3 w-full">
            <form
              data-testid="enquiry-form"
              onSubmit={submit}
              className="rounded-3xl bg-white border border-sky-100 shadow-lift p-7 lg:p-10"
            >
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label htmlFor="enq-name" className="block text-sm font-semibold text-slate-700 mb-2">
                    Your name
                  </label>
                  <input
                    id="enq-name"
                    data-testid="enquiry-input-name"
                    type="text"
                    required
                    value={form.name}
                    onChange={set('name')}
                    placeholder="Full name"
                    className="w-full rounded-xl border border-sky-200 bg-brand-cream/50 px-4 py-3.5 text-slate-800 placeholder:text-slate-400 focus:border-azure focus:outline-none focus:ring-4 focus:ring-cyan-400/15 transition-all"
                  />
                </div>
                <div>
                  <label htmlFor="enq-contact" className="block text-sm font-semibold text-slate-700 mb-2">
                    Phone or email
                  </label>
                  <input
                    id="enq-contact"
                    data-testid="enquiry-input-contact"
                    type="text"
                    required
                    value={form.contact}
                    onChange={set('contact')}
                    placeholder="How do we reach you?"
                    className="w-full rounded-xl border border-sky-200 bg-brand-cream/50 px-4 py-3.5 text-slate-800 placeholder:text-slate-400 focus:border-azure focus:outline-none focus:ring-4 focus:ring-cyan-400/15 transition-all"
                  />
                </div>
              </div>
              <div className="mt-5">
                <label htmlFor="enq-message" className="block text-sm font-semibold text-slate-700 mb-2">
                  How can we help?
                </label>
                <textarea
                  id="enq-message"
                  data-testid="enquiry-input-message"
                  required
                  rows={5}
                  value={form.message}
                  onChange={set('message')}
                  placeholder="Tell us a little about the support you or your loved one is looking for…"
                  className="w-full rounded-xl border border-sky-200 bg-brand-cream/50 px-4 py-3.5 text-slate-800 placeholder:text-slate-400 focus:border-azure focus:outline-none focus:ring-4 focus:ring-cyan-400/15 transition-all resize-none"
                />
              </div>
              <button
                data-testid="enquiry-submit"
                type="submit"
                disabled={sending}
                className="mt-7 w-full flex items-center justify-center gap-2.5 rounded-full bg-azure px-8 py-4 font-display font-bold text-white shadow-soft transition-all hover:bg-sky-700 hover:-translate-y-0.5 disabled:opacity-60 disabled:hover:translate-y-0"
              >
                {sending ? <Loader2 size={18} className="animate-spin" /> : <Send size={17} />}
                {sending ? 'Sending…' : 'Send enquiry'}
              </button>
              <p className="mt-4 text-center text-xs text-slate-500">
                Prefer to talk? Call us anytime on{' '}
                <a href="tel:0473619278" data-testid="enquiry-inline-phone" className="font-semibold text-azure hover:underline">
                  0473 619 278
                </a>
              </p>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
